import React, { memo, useEffect } from 'react';
import styled from 'styled-components';

import ExpandableContent from '@ui/expandableContent.tsx';
import { getContainerHeight } from '@utils/mapElement.ts';

import PoiHeader from './components/PoiHeader.tsx';
import { getSectionWidget } from './poiSections.ts';

const InnerLayoutStyled = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  height: 100%;
`;

const ScrollableContent = styled.div`
  padding: 0 12px 40px 12px;
  overflow-y: ${props => (props.$canScroll ? 'auto' : 'hidden')};
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
`;

const PoiHeaderStyled = styled(PoiHeader)`
  flex: 0 0 auto;
  padding: 2px 12px 0 12px;
`;

// The class version used shouldComponentUpdate to skip unrelated parent renders.
// memo keeps that behavior after converting to a function component.
export const InnerLayout = memo(
  ({
    poi,
    venueCategory,
    venueTimezone,
    openStatus,
    layout,
    className,
    T,
    lang,
    canScroll,
    structures,
    submitAnalyticsEvent,
    submitAnalyticsEventWithAppInsights,
    submitReserveParkingClick,
    searchInputsLength,
    hidePoiFeedback,
    venueId,
    notifyAboutChange,
    isOpen,
  }) => {
    useEffect(() => {
      notifyAboutChange();
    }, [notifyAboutChange, poi]);

    // stop propagation so it won't fire expandableContent resizing
    const stopPropagation = e => {
      if (isOpen) {
        e.stopPropagation();
      }
    };

    return (
      <InnerLayoutStyled data-testid="PoiViewWidgetMobile" className={className}>
        <PoiHeaderStyled poi={poi} T={T} />
        <ScrollableContent $canScroll={canScroll} onMouseMove={stopPropagation} onTouchMove={stopPropagation}>
          {layout.map(sectionName => {
            const $Component = getSectionWidget(sectionName);
            return $Component ? (
              <$Component
                key={sectionName}
                poi={poi}
                T={T}
                venueCategory={venueCategory}
                venueTimezone={venueTimezone}
                openStatus={openStatus}
                structures={structures}
                lang={lang}
                submitAnalyticsEvent={submitAnalyticsEvent}
                submitAnalyticsEventWithAppInsights={submitAnalyticsEventWithAppInsights}
                onSubmit={submitReserveParkingClick}
                searchInputsLength={searchInputsLength}
                hidePoiFeedback={hidePoiFeedback}
                venueId={venueId}
                mobile={true}
              />
            ) : null;
          })}
        </ScrollableContent>
      </InnerLayoutStyled>
    );
  },
);

const NavigationViewMobile = ({ bus, id, ...rest }) => {
  return (
    <ExpandableContent
      id={id}
      initialHeight={getContainerHeight() / 2 + 'px'}
      maxHeight={getContainerHeight() + 'px'}
      bus={bus}
      hasInnerScrollingElement={true}
      T={rest.T}
      poi={rest.poi}
    >
      <InnerLayout {...rest} />
    </ExpandableContent>
  );
};

export default NavigationViewMobile;
